import React, { FC, useState, useMemo } from 'react';
import { AnyFCO, FeatureServiceFCOFields, FCOFields, FCOType, FeatureServiceFCO } from '../../../core/types/fcoTypes';

import {
  ComboFilterCategory,
  ComboFilterDateSelector,
  ComboFilterHierarchicalList,
  ComboFilterHierarchyState,
  ComboFilterListSelector,
  EmptyProps,
  ExtendedTableColumn,
  Link,
  Select,
  Table,
  TableViewLayout,
  Text,
  useTableFilters,
} from '@lithos';

import {
  isInDateRange,
  getFilteredItems,
  tableStringSortParameters,
  tableArrayLengthSortParameters,
  tableDateSortParameters,
  tableBooleanSortParameters,
} from '@lithos/TableUtils';

import { isEqual, uniq } from 'lodash';

import { areAnyCategoryMembersCurrentlyActive, countHierarchyActiveSelections } from '@lithos/ComboFilterUtils';
import { defaultDateState, defaultRangeState, defaultTagsState } from '../../redesign/fcoListView/utils';
import RelatedFCOsCell from '../../redesign/fcoListView/RelatedFCOsCell';
import TagsCell from '../../redesign/fcoListView/TagsCell';
import MaterializationCell from '../../redesign/fcoListView/MaterializationCell';
import DateCell from '../../redesign/fcoListView/DateCell';
import TableColumnControls from '../../redesign/fcoListView/TableColumnControls';
import TableSelectors from '../../redesign/fcoListView/TableSelectors';
import { useUserSettings } from '../../context/UserSettingsContext';
import { FeatureFlags } from '../../context/UserSettingsContext';

interface FeatureServicesListProps {
  items: FeatureServiceFCO[];
  idToFcoMap: Record<string, AnyFCO>;
  workspace: string;
}

enum FeatureServicesListColumnKeys {
  NAME,
  TAGS,
  ONLINE,
  FEATURE_VIEWS,
  CREATED_BY,
  CREATED_AT,
  LAST_UPDATED_BY,
  LAST_UPDATED_AT,
  REALTIME_SERVER,
  SERVER_GROUP,
}

interface FeatureServicesListFilterState {
  tags: ComboFilterHierarchyState;
  online: string[];
  '# of Feature Views': [number, number];
  'Feature Views': string[];
  createdBy: string[];
  createdAt: [Date, Date];
  lastUpdatedBy: string[];
  lastUpdatedAt: [Date, Date];
  serverGroup: string[];
}

const FeatureServicesList: FC<FeatureServicesListProps> = ({ items, idToFcoMap, workspace }) => {
  const { featureEnabled } = useUserSettings();
  const canShowServerGroups = featureEnabled(FeatureFlags.SHOW_SERVER_GROUPS);

  // Always need to set a default filter state
  const defaultFilterState: FeatureServicesListFilterState = useMemo(
    (): FeatureServicesListFilterState => ({
      tags: defaultTagsState(items),
      online: [],
      '# of Feature Views': defaultRangeState(items, FeatureServiceFCOFields.DEPENDENT_FEATURE_VIEWS),
      'Feature Views': [],
      createdBy: [],
      createdAt: defaultDateState(items, FCOFields.CREATED_DATE),
      lastUpdatedBy: [],
      lastUpdatedAt: defaultDateState(items, FCOFields.LAST_MODIFIED_DATE),
      serverGroup: [],
    }),
    [items]
  );

  const allValidOptions = useMemo(() => {
    return {
      Tags: Array.from(Object.keys(defaultTagsState(items))),
      Online: ['true', 'false'],
      'Feature Views': uniq(items.map((item) => item[FeatureServiceFCOFields.DEPENDENT_FEATURE_VIEWS]).flat())
        .filter((id): id is string => id !== undefined)
        .map((id) => idToFcoMap[id]?.[FCOFields.NAME] ?? '')
        .filter(Boolean),
      Owner: uniq(items.map((item) => item[FCOFields.OWNER] ?? '')).filter(Boolean),
      'Last Updated By': uniq(items.map((item) => item[FCOFields.LAST_MODIFIED_BY] ?? '')).filter(Boolean),
      'Created At': uniq(items.map((item) => item[FCOFields.CREATED_DATE])).filter(Boolean),
      'Last Updated At': uniq(items.map((item) => item[FCOFields.LAST_MODIFIED_DATE])).filter(Boolean),
      'Server Group': uniq(items.map((item) => item[FeatureServiceFCOFields.TRANSFORM_SERVER_GROUP_NAME] ?? '')).filter(
        Boolean
      ),
    };
  }, [items, idToFcoMap]);

  // Shared logic and states for filtered table views
  const {
    searchQuery,
    hasAnyFilters,
    activeFilterCount,
    sortType,
    setSortType,
    activeHeading,
    setActiveHeading,
    filterState,
    onSearchInputChange,
    handleResetFilters,
    handleFilterChange,
  } = useTableFilters<FeatureServiceFCO, FeatureServicesListColumnKeys, FeatureServicesListFilterState>(
    defaultFilterState
  );

  // Filter components are the actual input components (e.g. A combo filter listt)
  // These are defined separately from the ComboFilters because they are reused in the table header
  // They may also be reused in shortcut popovers in the general filter list
  // This allows us to reference the same component in multiple places
  const filterComponents: Record<
    FeatureServicesListColumnKeys,
    {
      filter: React.ReactNode;
      activeCount: () => number;
      filterCallback: (featureView: FeatureServiceFCO) => boolean;
      name: string;
    }
  > = useMemo(() => {
    return {
      [FeatureServicesListColumnKeys.NAME]: {
        filter: <></>,
        activeCount: () => 0,
        filterCallback: () => true,
        name: 'Name',
      },
      [FeatureServicesListColumnKeys.TAGS]: {
        filter: (
          <ComboFilterHierarchicalList
            items={filterState.tags}
            setItems={(tags) => {
              handleFilterChange(filterComponents, 'tags', tags);
            }}
          />
        ),
        activeCount: () => countHierarchyActiveSelections(filterState.tags),
        filterCallback: (featureView) => {
          let hasEachCategoryMatched = true;
          const categories = Object.keys(filterState.tags);
          categories.forEach((category) => {
            if (areAnyCategoryMembersCurrentlyActive(filterState.tags[category])) {
              const activeSubcategories = Object.entries(filterState.tags[category])
                .filter((entry) => entry[1])
                .map((result) => result[0]);
              const hasMatch = featureView.tags.find(
                (tag) => tag.key === category && activeSubcategories.includes(tag.value)
              );
              if (hasMatch === undefined) {
                hasEachCategoryMatched = false;
              }
            }
          });

          return hasEachCategoryMatched;
        },
        name: 'Tags',
      },
      [FeatureServicesListColumnKeys.ONLINE]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState.online}
            setSelected={(online) => handleFilterChange(filterComponents, 'online', online)}
            items={['true', 'false']}
            title={''}
            customRenderer={(name) => {
              return <MaterializationCell materialization={name === 'true'} />;
            }}
          />
        ),
        activeCount: () => filterState.online.length,
        filterCallback: (featureView) => {
          const isEnabled = featureView[FeatureServiceFCOFields.IS_ONLINE_SERVING_ENABLED] ?? false;
          return filterState.online.includes(isEnabled.toString());
        },
        name: 'Online',
      },
      [FeatureServicesListColumnKeys.FEATURE_VIEWS]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState['Feature Views']}
            setSelected={(transformations) => handleFilterChange(filterComponents, 'Feature Views', transformations)}
            items={allValidOptions['Feature Views']}
            title={''}
          />
        ),
        activeCount: () => filterState['Feature Views'].length,
        filterCallback: (featureView) => {
          const featureViewIds = featureView[FeatureServiceFCOFields.DEPENDENT_FEATURE_VIEWS] ?? [];
          const featureViewNames = featureViewIds.map((id) => idToFcoMap[id]?.[FCOFields.NAME] ?? '').filter(Boolean);
          return filterState['Feature Views'].some((name) => featureViewNames.includes(name));
        },
        name: 'Feature Views',
        icon: 'FeatureView',
      },
      [FeatureServicesListColumnKeys.LAST_UPDATED_BY]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState.lastUpdatedBy}
            setSelected={(type) => handleFilterChange(filterComponents, 'lastUpdatedBy', type)}
            items={allValidOptions['Last Updated By']}
            title={''}
          />
        ),
        activeCount: () => filterState.lastUpdatedBy.length,
        filterCallback: (featureView) => {
          return filterState.lastUpdatedBy.includes(featureView[FCOFields.LAST_MODIFIED_BY] ?? '');
        },
        name: 'Last Updated By',
      },
      [FeatureServicesListColumnKeys.LAST_UPDATED_AT]: {
        filter: (
          <ComboFilterDateSelector
            start={filterState.lastUpdatedAt[0]}
            setStart={(date) => {
              handleFilterChange(filterComponents, 'lastUpdatedAt', [date as Date, filterState.lastUpdatedAt[1]]);
            }}
            end={filterState.lastUpdatedAt[1]}
            setEnd={(date) => {
              handleFilterChange(filterComponents, 'lastUpdatedAt', [filterState.lastUpdatedAt[0], date as Date]);
            }}
          />
        ),
        activeCount: () => (isEqual(filterState.lastUpdatedAt, defaultFilterState.lastUpdatedAt) ? 0 : 1),
        filterCallback: (featureView) => {
          return isInDateRange(featureView[FCOFields.LAST_MODIFIED_DATE], filterState.lastUpdatedAt);
        },
        name: 'Last Updated At',
      },
      [FeatureServicesListColumnKeys.CREATED_BY]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState.createdBy}
            setSelected={(type) => handleFilterChange(filterComponents, 'createdBy', type)}
            items={allValidOptions['Owner']}
            title={''}
          />
        ),
        activeCount: () => filterState.createdBy.length,
        filterCallback: (featureView) => {
          return filterState.createdBy.includes(featureView[FCOFields.OWNER] ?? '');
        },
        name: 'Owner',
      },
      [FeatureServicesListColumnKeys.CREATED_AT]: {
        filter: (
          <ComboFilterDateSelector
            start={filterState.createdAt[0]}
            setStart={(date) => {
              handleFilterChange(filterComponents, 'createdAt', [date as Date, filterState.createdAt[1]]);
            }}
            end={filterState.lastUpdatedAt[1]}
            setEnd={(date) => {
              handleFilterChange(filterComponents, 'createdAt', [filterState.createdAt[0], date as Date]);
            }}
          />
        ),
        activeCount: () => (isEqual(filterState.createdAt, defaultFilterState.createdAt) ? 0 : 1),
        filterCallback: (featureService) => {
          return isInDateRange(featureService[FCOFields.CREATED_DATE], filterState.createdAt);
        },
        name: 'Created At',
      },
      [FeatureServicesListColumnKeys.REALTIME_SERVER]: {
        filter: <></>,
        activeCount: () => 0,
        filterCallback: () => true,
        name: 'Realtime Server',
      },
      [FeatureServicesListColumnKeys.SERVER_GROUP]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState.serverGroup}
            setSelected={(serverGroup) => handleFilterChange(filterComponents, 'serverGroup', serverGroup)}
            items={allValidOptions['Server Group']}
            title={''}
          />
        ),
        activeCount: () => filterState.serverGroup.length,
        filterCallback: (featureView) => {
          return filterState.serverGroup.includes(
            featureView[FeatureServiceFCOFields.TRANSFORM_SERVER_GROUP_NAME] ?? ''
          );
        },
        name: 'Server Group',
      },
    };
  }, [filterState, idToFcoMap, defaultFilterState, handleFilterChange, allValidOptions]);

  // These define the filter categories in the Filter button popover
  const categories: ComboFilterCategory[] = [
    {
      name: 'Tags',
      icon: 'Tag',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'tags', defaultFilterState.tags);
      },
      activeCount: countHierarchyActiveSelections(filterState.tags),
      popover: filterComponents[FeatureServicesListColumnKeys.TAGS].filter,
      disabled: allValidOptions['Tags'].length === 0,
    },

    {
      name: 'Online',
      icon: 'FeatureView',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'online', defaultFilterState.online);
      },
      activeCount: filterState.online.length,
      popover: filterComponents[FeatureServicesListColumnKeys.ONLINE].filter,
    },

    {
      name: '# of Feature Views',
      icon: 'FeatureView',
      resetCallback: () => {
        handleFilterChange(filterComponents, '# of Feature Views', defaultFilterState['# of Feature Views']);
      },
      activeCount: isEqual(filterState['# of Feature Views'], defaultFilterState['# of Feature Views']) ? 0 : 1,
      popover: filterComponents[FeatureServicesListColumnKeys.FEATURE_VIEWS].filter,
    },
    {
      name: 'Feature Views',
      icon: 'FeatureView',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'Feature Views', defaultFilterState['Feature Views']);
      },
      activeCount: filterState['Feature Views'].length,
      popover: filterComponents[FeatureServicesListColumnKeys.FEATURE_VIEWS].filter,
      disabled: allValidOptions['Feature Views'].length === 0,
    },
    {
      name: 'Owner',
      icon: 'UserCircle',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'createdBy', defaultFilterState.createdBy);
      },
      activeCount: filterState.createdBy.length,
      popover: filterComponents[FeatureServicesListColumnKeys.CREATED_BY].filter,
      disabled: allValidOptions['Owner'].length === 0,
    },
    {
      name: 'Created At',
      icon: 'Calendar',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'createdAt', defaultFilterState.createdAt);
      },
      activeCount: isEqual(filterState.createdAt, defaultFilterState.createdAt) ? 0 : 1,
      popover: filterComponents[FeatureServicesListColumnKeys.CREATED_AT].filter,
    },
    {
      name: 'Last Updated By',
      icon: 'UserCircle',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'lastUpdatedBy', defaultFilterState.lastUpdatedBy);
      },
      activeCount: filterState.lastUpdatedBy.length,
      popover: filterComponents[FeatureServicesListColumnKeys.LAST_UPDATED_BY].filter,
      disabled: allValidOptions['Last Updated By'].length === 0,
    },
    {
      name: 'Last Updated At',
      icon: 'Calendar',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'lastUpdatedAt', defaultFilterState.lastUpdatedAt);
      },
      activeCount: isEqual(filterState.lastUpdatedAt, defaultFilterState.lastUpdatedAt) ? 0 : 1,
      popover: filterComponents[FeatureServicesListColumnKeys.LAST_UPDATED_AT].filter,
    },
  ];

  // Define the columns for the table
  const columns: ExtendedTableColumn<FeatureServiceFCO>[] = [
    {
      name: 'Name',
      id: FeatureServicesListColumnKeys.NAME,
      content: (featureService) => (
        <Link
          name={featureService[FCOFields.NAME]}
          icon="FeatureService"
          url={`/repo/${workspace}/feature-services/${featureService[FCOFields.NAME]}/overview`}
          testId={`feature-services-link-${featureService[FCOFields.NAME]}`}
        />
      ),
      columnWidth: 'l',
      ...tableStringSortParameters(sortType, FCOFields.NAME, setSortType),
    },
    {
      name: 'Tags',
      id: FeatureServicesListColumnKeys.TAGS,
      content: (featureService) => <TagsCell fco={featureService} />,
      filterContent: filterComponents[FeatureServicesListColumnKeys.TAGS].filter,
      ...tableArrayLengthSortParameters(sortType, FCOFields.TAGS, setSortType),
    },

    {
      name: 'Online',
      id: FeatureServicesListColumnKeys.ONLINE,
      content: (featureService) => {
        if (featureService[FeatureServiceFCOFields.IS_ONLINE_SERVING_ENABLED] === undefined) {
          return <Text size="s">n/a</Text>;
        }
        return (
          <MaterializationCell
            materialization={featureService[FeatureServiceFCOFields.IS_ONLINE_SERVING_ENABLED] ?? false}
          />
        );
      },
      columnWidth: 's',
      filterContent: filterComponents[FeatureServicesListColumnKeys.ONLINE].filter,
      ...tableBooleanSortParameters(sortType, FeatureServiceFCOFields.IS_ONLINE_SERVING_ENABLED, setSortType),
    },

    {
      name: 'Feature Views',
      id: FeatureServicesListColumnKeys.FEATURE_VIEWS,
      content: (featureService) => {
        const relatedFeatureServices = featureService[FeatureServiceFCOFields.DEPENDENT_FEATURE_VIEWS].map(
          (id) => idToFcoMap[id]
        );
        return <RelatedFCOsCell workspace={workspace} fcos={relatedFeatureServices} type={FCOType.FEATURE_VIEW} />;
      },
      filterContent: filterComponents[FeatureServicesListColumnKeys.FEATURE_VIEWS].filter,
      ...tableArrayLengthSortParameters(sortType, FeatureServiceFCOFields.DEPENDENT_FEATURE_VIEWS, setSortType),
      columnWidth: 'm',
    },
    {
      name: 'Last Updated By',
      id: FeatureServicesListColumnKeys.LAST_UPDATED_BY,
      content: (featureService) => (
        <Link
          avatar={{ name: featureService[FCOFields.LAST_MODIFIED_BY] }}
          name={featureService[FCOFields.LAST_MODIFIED_BY]}
          noHover
        />
      ),
      filterContent: filterComponents[FeatureServicesListColumnKeys.LAST_UPDATED_BY].filter,
      columnWidth: 'm',
      ...tableStringSortParameters(sortType, FCOFields.LAST_MODIFIED_BY, setSortType),
    },
    {
      name: 'Last Updated At',
      id: FeatureServicesListColumnKeys.LAST_UPDATED_AT,
      content: (featureService) => <DateCell date={featureService[FCOFields.LAST_MODIFIED_DATE]} />,
      filterContent: filterComponents[FeatureServicesListColumnKeys.LAST_UPDATED_AT].filter,
      ...tableDateSortParameters(sortType, FCOFields.LAST_MODIFIED_DATE, setSortType),
      columnWidth: 'm',
    },
    {
      name: 'Owner',
      id: FeatureServicesListColumnKeys.CREATED_BY,
      content: (featureService) => (
        <Link noHover avatar={{ name: featureService[FCOFields.OWNER] }} name={featureService[FCOFields.OWNER]} />
      ),
      filterContent: filterComponents[FeatureServicesListColumnKeys.CREATED_BY].filter,
      ...tableStringSortParameters(sortType, FCOFields.OWNER, setSortType),
    },
    {
      name: 'Created At',
      id: FeatureServicesListColumnKeys.CREATED_AT,
      content: (featureService) => <DateCell date={featureService[FCOFields.CREATED_DATE]} />,
      filterContent: filterComponents[FeatureServicesListColumnKeys.CREATED_AT].filter,
      ...tableDateSortParameters(sortType, FCOFields.CREATED_DATE, setSortType),
      columnWidth: 'm' as const,
    },
    ...(canShowServerGroups
      ? [
          {
            name: 'Transform Server Group',
            id: FeatureServicesListColumnKeys.SERVER_GROUP,
            content: (featureService: FeatureServiceFCO) =>
              featureService[FeatureServiceFCOFields.TRANSFORM_SERVER_GROUP_NAME] === undefined ? (
                <>-</>
              ) : (
                <Link
                  icon="TransformServerGroup"
                  name={featureService[FeatureServiceFCOFields.TRANSFORM_SERVER_GROUP_NAME]}
                  url={`/repo/${workspace}/server-groups/transform/${
                    featureService[FeatureServiceFCOFields.TRANSFORM_SERVER_GROUP_ID]
                  }`}
                />
              ),
            columnWidth: 'l' as const,
          },
        ]
      : []),
  ];

  const selectableColumns = columns.map((column) => {
    return { name: column.name as string, visible: true };
  });

  const [columnState, setColumnState] = useState<{ name: string; visible: boolean }[]>(selectableColumns);

  const columnsSelector = (
    <Select
      groups={[
        {
          items: columnState.map((column) => {
            return {
              name: column.name,
              asToggle: true,
              onClick: () => {
                const newColumnState = columnState.map((selectableColumn) => {
                  if (selectableColumn.name !== column.name) {
                    return selectableColumn;
                  }

                  return {
                    name: selectableColumn.name,
                    visible: !selectableColumn.visible,
                  };
                });
                setColumnState && setColumnState(newColumnState);
              },
              selected: column.visible,
              selectable: true,
            };
          }),
        },
      ]}
      filterable={false}
    />
  );

  const filterEmpty: EmptyProps = {
    actions: <></>,
    placement: 'full-page',
    useCase: 'empty',
    title: 'No Results',
    description: 'No Feature Views match the current filters. Try adjusting your filters or search query.',
  };

  const filteredColumns = columns
    .map((column) => {
      const matching = columnState.find((stateColumn) => stateColumn.name === column.name);
      if (!matching) {
        return;
      }
      if (matching?.visible !== true) {
        return;
      }

      const asColumn: ExtendedTableColumn<FeatureServiceFCO> = {
        ...column,
        isActive: column.id === activeHeading,
        setIsActive: (arg) => {
          if (arg) {
            setActiveHeading(column.id as FeatureServicesListColumnKeys);
            return;
          }
          setActiveHeading(undefined);
        },
        hideCallback: () => {
          const newColumnState = columnState.map((selectableColumn) => {
            if (selectableColumn.name !== column.name) {
              return selectableColumn;
            }

            return {
              name: selectableColumn.name,
              visible: !selectableColumn.visible,
            };
          });

          setColumnState && setColumnState(newColumnState);
        },
        columnsContent: columnsSelector,
      };

      return asColumn;
    })
    .filter((column) => column !== undefined) as ExtendedTableColumn<FeatureServiceFCO>[];

  const activeFilters = Object.values(filterComponents).filter((filter) => {
    return filter.activeCount() !== 0;
  });

  const filteredResults = getFilteredItems(
    items,
    activeFilters,
    [FCOFields.NAME, FCOFields.ID],
    searchQuery ?? '',
    sortType
  );

  return (
    <TableViewLayout
      searchInput={
        <TableSelectors
          hasAnyFilters={hasAnyFilters}
          activeFilterCount={activeFilterCount}
          handleResetFilters={handleResetFilters}
          categories={categories}
          onSearchInputChange={onSearchInputChange}
        />
      }
      filterAndColumnControls={<TableColumnControls columnsSelector={columnsSelector} />}
      table={
        <>
          <Table
            items={filteredResults}
            columns={filteredColumns}
            frozenFirstColumn={true}
            emptyProps={filterEmpty}
            memoized={true}
          />
        </>
      }
    />
  );
};

export default FeatureServicesList;
